import React, { useEffect, useState, useRef } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { duplicateTemplateToAgent } from "@/services/templateService";
import { useToast } from "@/hooks/use-toast";
import { analytics } from "@/hooks/useAnalytics";

const AgentCreate = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, organization } = useAuth();
  const { toast } = useToast();
  const templateData = location.state?.templateData;
  const [status, setStatus] = useState("Processing template...");
  const hasCreated = useRef(false);

  useEffect(() => {
    if (hasCreated.current) return;
    hasCreated.current = true;
    const initAgentCreation = async () => {
      if (!templateData || !user || !organization) {
        console.log("Missing required data:", {
          templateData: !!templateData,
          user: !!user,
          organization: !!organization
        });
        return;
      }

      try {
        console.log("Creating agent from template with ID:", templateData.id);
        console.log("Template public_id:", templateData.public_id);
        setStatus("Creating new agent...");
        const result = await duplicateTemplateToAgent(templateData.id, user.id, organization.id);

        if (!result.success) {
          console.error("Error response from duplicateTemplateToAgent:", result.error);
          throw new Error(result.error || "Failed to create agent from template");
        }

        if (!result.agentId) {
          console.error("No agent ID returned from duplicateTemplateToAgent");
          throw new Error("Agent creation returned no agent ID");
        }

        console.log("Agent creation successful, ID:", result.agentId);
        setStatus("Success! Redirecting...");

        // Track agent creation in Segment
        analytics.trackAgentCreated({
          agentId: result.agentId,
          agentName: templateData.title,
          publicId: result.agentPublicId,
          templateId: templateData.id,
          templateName: templateData.title,
          category: templateData.category || 'unknown',
          organizationId: organization.id
        });

        // Navigate to the new agent
        toast({
          title: "Agent Created",
          description: `${templateData.title} has been created successfully.`,
        });

        // Navigate to the agent detail page using the ID from the created agent
        navigate(`/agents/${result.agentId}`);
      } catch (error: unknown) {
        console.error("Error creating agent from template:", error);
        setStatus("Error creating agent");
        toast({
          variant: "destructive",
          title: "Failed to create agent",
          description: error instanceof Error ? error.message : "An unexpected error occurred.",
        });
        navigate("/agents");
      }
    };

    initAgentCreation();
  }, [templateData, user, organization, navigate, toast]);

  // If no template data is available, redirect to agents list
  if (!templateData) {
    console.log("No template data provided, redirecting to /agents");
    return <Navigate to="/agents" replace />;
  }

  // Show loading while creating agent
  return (
    <div className="flex items-center justify-center h-screen">
      <div className="text-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4"></div>
        <p>{status}</p>
        <p className="text-sm text-muted-foreground mt-2">Creating "{templateData.title}" agent...</p>
      </div>
    </div>
  );
};

export default AgentCreate;
